import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPaginatedProjectList,
  getProjectBySearch,
  getProjectTimeEntries,
} from "../API/index";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import ProjectTable from "./ProjectTable";
import ArrowLeft from "../assets/images/left-arrow.png";

const PROJECT_PAGE_SIZE = 25;
const INITIAL_PROJECT_PAGINATION = {
  take: PROJECT_PAGE_SIZE,
  skip: 0,
};

const ProjectsList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(INITIAL_PROJECT_PAGINATION);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedProjects, setSearchedProjects] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // const [error, setError] = useState(false);
  const navigate = useNavigate();
  let tableHeadings = ["Project id", "Description", "Status", "Action"];

  const fetchProjectListWithIsDeletable = useCallback(async (data) => {
    const filteredProjects = await Promise.all(
      data?.data?.map(async (project) => {
        if (project?.type === "webtime") {
          const queryParams = `project_id=${project?.id}`;
          const tempProjects = await getProjectTimeEntries(queryParams);
          if (tempProjects?.data?.length) {
            return {
              ...project,
              isDeletable: false,
            };
          } else {
            return {
              ...project,
              isDeletable: true,
            };
          }
        } else {
          return {
            ...project,
            isDeletable: false,
          };
        }
      })
    );
    return filteredProjects;
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        try {
          const res = await getProjectBySearch(query);
          if (res?.data) {
            const filteredProjects = await fetchProjectListWithIsDeletable(res);
            setSearchedProjects(filteredProjects);
          }
        } catch (error) {
          setSearchedProjects([]);
        } finally {
          setSearchLoading(false);
        }
      }, 1500),
    [fetchProjectListWithIsDeletable]
  );

  useEffect(() => {
    if (searchQuery.length >= 2) {
      setSearchLoading(true);
      setSearchedProjects([]);
      debouncedSearch(searchQuery);
    } else {
      setSearchLoading(false);
      setSearchedProjects([]);
      debouncedSearch.cancel();
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const getAllProjectData = async (next) => {
    if (next) {
      setPaginationLoading(true);
      try {
        const payload = {
          take: pagination?.take,
          skip: pagination?.skip + PROJECT_PAGE_SIZE,
        };
        setPagination(payload);
        const data = await getPaginatedProjectList(payload);
        if (data) {
          const filteredProjects = await fetchProjectListWithIsDeletable(data);
          setPaginationLoading(false);
          setList((prevState) => [...prevState, ...filteredProjects]);
          setLoading(false);
        }
      } catch (error) {
        setPaginationLoading(false);
        setLoading(false);
      }
    } else {
      try {
        const data = await getPaginatedProjectList(pagination);
        if (data) {
          const filteredProjects = await fetchProjectListWithIsDeletable(data);
          setList(filteredProjects);
          setLoading(false);
          // await Promise.all(filteredProjects);
        }
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const detailHandler = (id) => {
    navigate(`/project-details/${id}`, {
      state: {
        id: id,
      },
    });
  };

  useEffect(() => {
    const fetchInitialProjectData = async () => {
      try {
        const data = await getPaginatedProjectList(INITIAL_PROJECT_PAGINATION);
        if (data) {
          const filteredProjects = await fetchProjectListWithIsDeletable(data);
          setList(filteredProjects);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchInitialProjectData();
  }, [fetchProjectListWithIsDeletable]);

  return (
    <div className="crew-mgmt project-details-page project-list-page">
      <div className="project-details-toolbar">
        <button
          className="btn btn-primary back-btn project-details-back-btn"
          onClick={() => navigate(-1)}
          type="button"
        >
          <img src={ArrowLeft} alt="Back" />
        </button>
        <h1 className="title project-details-title">Projects</h1>
        <div className="project-details-toolbar__actions">
          <div className="search-container">
            <form className="form-wo" onSubmit={(event) => event.preventDefault()}>
              <button type="button" aria-label="Search projects">
                <svg
                  width="17"
                  height="16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M7.667 12.667A5.333 5.333 0 107.667 2a5.333 5.333 0 000 10.667zM14.334 14l-2.9-2.9"
                    stroke="currentColor"
                    strokeWidth="1.333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </button>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                style={{
                  border: "0",
                  background: "transparent",
                  fontSize: "13px",
                  width: "150px",
                  padding: "0.5rem 0",
                }}
              />
              <button
                className="reset"
                type="reset"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
      <ProjectTable
        list={list}
        tableHeadings={tableHeadings}
        detailHandler={detailHandler}
        loading={loading}
        getAllProjectData={getAllProjectData}
        paginationLoading={paginationLoading}
        searchQuery={searchQuery}
        searchedProjects={searchedProjects}
        searchLoading={searchLoading}
      />
    </div>
  );
};

export default ProjectsList;
